import { createSlice } from '@reduxjs/toolkit';
import { createAppAsyncThunk } from '../createAppAsyncThunk';
import { API_FETCH } from '../../services';
import type { RootState } from '../store';
import { env } from '../../env';
import { ENDPOINTS } from '../../constants/endPoints';
import { UserDetails } from '../../models/UserModel';
import lang from '../../languages/en.json';

interface ApiErrorPayload {
  message?: string;
  response?: {
    data?: {
      error?: string;
    };
  };
}

interface UserState {
  userDetails: UserDetails | null;
  userLoading: boolean;
  authError: string | null;
  isMultiStoreEnabled: boolean;
  suppressGenericErrorMessage: boolean;
}

const initialState: UserState = {
  userDetails: null,
  userLoading: false,
  authError: null,
  isMultiStoreEnabled: false,
  suppressGenericErrorMessage: false,
};

export const fetchUserDetailsFromSession = createAppAsyncThunk(
  'user/fetchUserDetailsFromSession',
  async () => {
    const response: UserDetails = await API_FETCH(
      `${env.RAD_APP_API_URL}${ENDPOINTS.RETRIEVE_USER_DETAILS}`,
    );
    return response;
  },
);

// Trying to redirect to RAD with a non-active or NCF selected store will return currentStoreNumber and userChainList as null/empty,
// so we need to check for both conditions to determine if the user has a valid store context
// If not valid then display error message
const hasNoValidStoreContext = (userDetails: UserDetails) =>
  !userDetails.currentStoreNumber ||
  (!userDetails.currentStoreNumber &&
    (!userDetails.userChainList || userDetails.userChainList.length === 0));

const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    setCurrentStoreNumber: (state, action: { payload: string }) => {
      if (state.userDetails) {
        state.userDetails.currentStoreNumber = action.payload;
      }
    },
    setMockUserData: (state, action: { payload: UserDetails }) => {
      state.userDetails = action.payload;
      state.userLoading = false;
      if (hasNoValidStoreContext(action.payload)) {
        state.authError = lang.commonErrors.noValidStoreError;
        state.suppressGenericErrorMessage = true;
      }
    },
    setTokenExpiredError: state => {
      state.authError = lang.commonErrors.tokenExpiredError;
    },
    setIsMultiStoreEnabled: (state, action: { payload: boolean }) => {
      state.isMultiStoreEnabled = action.payload;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchUserDetailsFromSession.pending, state => {
        state.userLoading = true;
      })
      .addCase(fetchUserDetailsFromSession.fulfilled, (state, action) => {
        state.userLoading = false;
        state.userDetails = action.payload;
        if (hasNoValidStoreContext(action.payload)) {
          state.authError = lang.commonErrors.noValidStoreError;
          state.suppressGenericErrorMessage = true;
        }
      })
      .addCase(fetchUserDetailsFromSession.rejected, (state, action) => {
        state.userLoading = false;
        const errorPayload = action.payload as ApiErrorPayload | undefined;
        if (errorPayload?.message && errorPayload?.response?.data?.error) {
          state.authError = `${errorPayload.message}: ${errorPayload.response.data.error}`;
        } else if (errorPayload?.response?.data?.error) {
          state.authError = errorPayload.response.data.error;
        } else if (errorPayload?.message) {
          state.authError = errorPayload.message;
        } else {
          state.authError = lang.commonErrors.authenticationError;
        }
      });
  },
});

// Selectors
export const selectUserDetails = (state: RootState) => state.user.userDetails;
export const selectAuthError = (state: RootState) => state.user.authError;
export const selectUserLoading = (state: RootState) => state.user.userLoading;
export const selectSuppressGenericErrorMessage = (state: RootState) =>
  state.user.suppressGenericErrorMessage;

// Actions
export const {
  setCurrentStoreNumber,
  setMockUserData,
  setTokenExpiredError,
  setIsMultiStoreEnabled,
} = userSlice.actions;
export const selectIsMultiStoreEnabled = (state: RootState) => state.user.isMultiStoreEnabled;

// True when the user's chain has one store or less — used to hide multi-store-only options.
// Note: distinct from isMultiStoreEnabled (the user-toggled view mode).
export const selectIsSingleStoreUser = (state: RootState) => {
  const chainList = state.user.userDetails?.userChainList;
  return !chainList || chainList.length <= 1;
};

export default userSlice.reducer;
